<script lang="ts" setup>
import { ref, onMounted, watch, toRef } from 'vue'
import { collection, query, orderBy, getDocs, where } from 'firebase/firestore'
import { dbRegistros } from '@/firebase/config'
import { convertFirestoreTimestamp } from '@/components/utils/functions'
import BaseModal from '../BaseModal.vue'
import { IFactura } from '@shared/types'
import FacturaCard from './FacturaCard.vue'
interface IFacturaWithId extends IFactura {
	id: string
}
const props = defineProps<{
	idInstitution: string
}>()
const error = ref<string>('')
const idInstitution = toRef(props, 'idInstitution')
const showErrorModal = ref(false)
const showSuccessModal = ref(false)
const successMessage = ref<string>('')
const facturas = ref<IFacturaWithId[]>([])

const obtenerFacturas = async (): Promise<void> => {
	const facturasRef = collection(dbRegistros, 'facturas')
	const q = query(facturasRef, orderBy('dateFactura', 'asc'), where('idInstitution', '==', props.idInstitution))

	const querySnapshot = await getDocs(q)

	if (querySnapshot.empty) {
		return
	}

	querySnapshot.forEach(doc => {
		const data = doc.data() as IFacturaWithId
		facturas.value.push({
			...data,
			dateFactura: convertFirestoreTimestamp(data.dateFactura),
			id: doc.id
		})
	})
}

// Cargar facturas cuando el componente se monte
onMounted(async () => {
	if (idInstitution.value) {
		await obtenerFacturas()
	}
})

// Cargar facturas cuando cambia la institución
watch(idInstitution, async () => {
	if (idInstitution.value) {
		facturas.value = []
		await obtenerFacturas()
	}
})
</script>
<template>
	<va-card class="bitacora-card">
		<va-card-title class="header-gradient">
			<div class="header-content">
				<div class="header-main">
					<div class="header-icon-wrapper">
						<va-icon name="receipt_long" class="header-icon" />
					</div>
					<div class="header-text">
						<h2 class="header-title">Historial</h2>
					</div>
				</div>
			</div>
		</va-card-title>

		<va-card-content class="card-content">
			<div class="collapse-body">
				<div class="collapse-content">
					<div class="pagos-container">
						<div v-for="factura in facturas" :key="factura.dateFactura">
							<FacturaCard :factura="factura" class="factura-card" />
						</div>
					</div>
				</div>
			</div>
		</va-card-content>
	</va-card>
	<BaseModal v-model="showErrorModal" title="Error" :message="error" :testMode="false" size="medium">
		<template #body>
			<div class="error-message">
				<div class="error-header">
					<div class="error-icon">
						<i class="fas fa-exclamation-triangle"></i>
					</div>
					<h3 class="error-title">Error</h3>
				</div>
				<p class="error-description">{{ error }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showErrorModal = false">Entendido</button>
		</template>
	</BaseModal>
	<BaseModal v-model="showSuccessModal" title="" :message="successMessage" :test-mode="false" size="medium">
		<template #body>
			<div class="success-message">
				<div class="success-header">
					<div class="success-icon">
						<i class="fas fa-check-circle"></i>
					</div>
					<h3 class="success-title">Éxito</h3>
				</div>
				<p class="success-description">{{ successMessage }}</p>
			</div>
		</template>
		<template #footer>
			<button class="btn btn-primary" @click="showSuccessModal = false">Entendido</button>
		</template>
	</BaseModal>
</template>
<style scoped>
.error-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #f71d1d;
}
.error-message {
	background: linear-gradient(135deg, #ecbaba 0%, #e67a7a 100%);
	border: 2px solid #f71d1d;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInError 0.4s ease-out;
}

.error-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #f68484;
}

.error-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #e67a7a 0%, #e67a7a 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.error-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.error-title {
	margin: 0;
	color: #e67a7a;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}
:deep(.dp__input) {
	background: var(--va-background-element);
	border: 1px solid var(--va-background-border);
	border-radius: 50px;
}

:deep(.dp__input)::placeholder {
	color: rgba(255, 255, 255, 0.7);
}
.success-description {
	font-size: 1.1rem;
	line-height: 1.6;
	margin-bottom: 1.5rem;
	padding: 1rem;
	background: rgba(255, 255, 255, 0.7);
	border-radius: 12px;
	border-left: 4px solid #28a745;
}
.success-message {
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border: 2px solid #28a745;
	border-radius: 16px;
	padding: 1.5rem;
	margin: 1rem 0;
	box-shadow: 0 8px 32px rgba(254, 178, 178, 0.3);
	animation: slideInSuccess 0.4s ease-out;
}

.success-header {
	display: flex;
	align-items: center;
	gap: 1rem;
	margin-bottom: 1.5rem;
	padding-bottom: 1rem;
	border-bottom: 2px solid #ffffff;
}

.success-icon {
	width: 48px;
	height: 48px;
	background: linear-gradient(135deg, #28a745 0%, #28a745 100%);
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 4px 16px rgba(245, 101, 101, 0.4);
}

.success-icon i {
	color: white;
	font-size: 1.5rem;
	animation: pulse 2s infinite;
}

.success-title {
	margin: 0;
	color: #ffffff;
	font-size: 1.5rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
}

/* Header mejorado */
.header-gradient {
	background: linear-gradient(135deg, #062342 0%, #062342 100%);
	padding: 0.5rem;
	border-radius: 0;
	position: relative;
	overflow: hidden;
	flex-shrink: 0;
}

.header-gradient::before {
	content: '';
	position: absolute;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, transparent 100%);
	pointer-events: none;
}

.header-content {
	position: relative;
	z-index: 1;
}

.header-main {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.header-icon-wrapper {
	width: 34px;
	height: 34px;
	border-radius: 16px;
	background: rgba(255, 255, 255, 0.2);
	backdrop-filter: blur(10px);
	display: flex;
	align-items: center;
	justify-content: center;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
	transition: all 0.3s ease;
}

.header-icon-wrapper:hover {
	transform: scale(1.1) rotate(5deg);
	background: rgba(255, 255, 255, 0.3);
}

.header-icon {
	color: white;
	font-size: 2rem;
}

.header-text {
	flex: 1;
}

.header-title {
	font-size: 1.75rem;
	font-weight: 700;
	color: white;
	margin: 0;
	text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
	letter-spacing: -0.5px;
}

:deep(.scrollable-table .va-data-table) {
	width: 100%;
	height: auto;
}

:deep(.scrollable-table .va-data-table__table-wrapper) {
	overflow: visible !important;
	height: auto !important;
}

:deep(.scrollable-table table) {
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table) {
	background: transparent;
	width: 100%;
}

:deep(.scrollable-table .va-data-table__table thead) {
	background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
	position: sticky;
	top: 0;
	z-index: 10;
}

:deep(.scrollable-table .va-data-table__table thead th) {
	padding: 1rem 1.25rem;
	font-weight: 700;
	color: #1a202c;
	font-size: 0.875rem;
	text-transform: uppercase;
	letter-spacing: 0.5px;
	border-bottom: 2px solid #e2e8f0;
}

:deep(.scrollable-table .va-data-table__table tbody tr) {
	transition: all 0.2s ease;
	border-bottom: 1px solid #f1f5f9;
}

:deep(.scrollable-table .va-data-table__table tbody tr:hover) {
	background: linear-gradient(90deg, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0.02) 100%);
	transform: translateX(2px);
	box-shadow: 0 2px 8px rgba(37, 99, 235, 0.1);
}

:deep(.scrollable-table .va-data-table__table tbody td) {
	padding: 1.25rem 1.25rem;
	color: #475569;
	font-size: 0.9375rem;
	vertical-align: middle;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@media (max-width: 768px) {
	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}

	:deep(.scrollable-table .va-data-table__table thead th),
	:deep(.scrollable-table .va-data-table__table tbody td) {
		padding: 0.75rem 0.5rem;
		font-size: 0.8125rem;
	}
}

@media (max-width: 480px) {
	.header-gradient {
		padding: 1.25rem;
	}
}

.bitacora-card {
	border-radius: 20px;
	padding: 1rem;
	box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
	animation: fadeInUp 0.6s ease-out;
	overflow: hidden;
	background: white;
	border: none;
	width: 100%;
	display: flex;
	flex-direction: column;
}

.card-content {
	padding: 2.5rem;
	padding-bottom: 3rem;
	background: #fafbfc;
	overflow-y: auto;
	max-height: calc(100vh - 200px);
}

/* Scrollbar personalizado para el contenido */
.card-content::-webkit-scrollbar {
	width: 8px;
}

.card-content::-webkit-scrollbar-track {
	background: #f1f1f1;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb {
	background: #888;
	border-radius: 4px;
}

.card-content::-webkit-scrollbar-thumb:hover {
	background: #555;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}
}

/* Asegurar que la card dentro de VaTabs ocupe todo el ancho */
:deep(.va-tabs) {
	width: 100%;
}

:deep(.va-tabs__content) {
	width: 100%;
	display: block;
}

:deep(.va-tabs__content > .va-card) {
	width: 100% !important;
	max-width: 100% !important;
	margin-left: 0 !important;
	margin-right: 0 !important;
}

/* Animaciones */
@keyframes fadeInUp {
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes fadeInScale {
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
}

@keyframes slideInLeft {
	from {
		opacity: 0;
		transform: translateX(-20px);
	}
	to {
		opacity: 1;
		transform: translateX(0);
	}
}

@keyframes pulse {
	0%,
	100% {
		transform: scale(1);
	}
	50% {
		transform: scale(1.1);
	}
}

@keyframes shine {
	0% {
		transform: translateX(-100%) translateY(-100%) rotate(45deg);
	}
	100% {
		transform: translateX(100%) translateY(100%) rotate(45deg);
	}
}

@keyframes float {
	0%,
	100% {
		transform: translateY(0);
	}
	50% {
		transform: translateY(-10px);
	}
}

@keyframes spin {
	0% {
		transform: rotate(0deg);
	}
	100% {
		transform: rotate(360deg);
	}
}

@media (max-width: 768px) {
	.card-content {
		padding: 1.5rem;
		padding-bottom: 2rem;
	}

	.header-gradient {
		padding: 1.5rem;
	}

	.header-main {
		flex-direction: column;
		align-items: flex-start;
		gap: 1rem;
	}

	.header-icon-wrapper {
		width: 56px;
		height: 56px;
	}

	.header-icon {
		font-size: 1.75rem;
	}

	.header-title {
		font-size: 1.5rem;
	}
}

@media (max-width: 480px) {
	.card-content {
		padding: 1.25rem;
		padding-bottom: 1.75rem;
	}

	.header-gradient {
		padding: 1.25rem;
	}
}

.collapse-body {
	border: 2px solid var(--va-background-border);
	border-top: none;
	border-radius: 0 0 12px 12px;
	background: var(--va-background-element);
}

.collapse-content {
	padding: 1.25rem;
	color: var(--va-text-secondary, #4a5568);
	line-height: 1.6;
}
.pagos-container {
	min-height: 500px;
}
</style>
